'use client'

import { ChangeEvent, useEffect, useState } from 'react'

import {
  Modal,
  Input,
  Button,
  OptionButton,
  Divider,
  Checkbox,
  composeClasses,
  GoogleIcon,
  Flex,
  stringNumberFormatToSimpleNumber,
  MACROBOLSA_ASSOCIATION_FILTER
} from '@wiggot/web-components'
import { DetailCounter } from '@/components'
import { AMENITIES_LIST } from '@/utils/constants'
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider/AmplitudeProvider'
import { getMacrobolsaUser } from '@wiggot/apis-ssr/utils/macrobolsa/get-macrobolsa-user-status-client'
import { AmplitudeEvents } from '@/interfaces/enum/amplitudeEvents.enum'

import { IFilters } from '@/hooks/useFilters'
import { Associations } from '@/components/Associations'
import { IAssociation } from '@/interfaces/search.interface'
import { Switch } from '@dd3tech/wiggui/Switch'
import { Spinner } from '@dd3tech/wiggui'

interface IMoreFiltersModal {
  isOpen: boolean
  onClose: () => void
  filters: any
  setFilters: (filters: any) => void
  applyMoreFilters: () => void
  resetMoreFilters: () => void
  associations: IAssociation[]
  onChangeAssociations: (value: any) => void
}

const SectionTitle = ({ title }: { title: string }) => (
  <div className="w-full my-2 sm:my-0">
    <p className="text-md font-semibold">{title}</p>
  </div>
)

export const MoreFiltersModal = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  applyMoreFilters,
  resetMoreFilters,
  associations,
  onChangeAssociations
}: IMoreFiltersModal) => {
  const { hasMacrobolsaNotAdvancedUser, hasMacrobolsaUser, isAdvance } =
    getMacrobolsaUser()
  const { trackEvent, isInitialized } = useAmplitude()
  const [restartToggle, setRestartToggle] = useState(false)
  const [associationsFilter, setAssociationsFilter] = useState<IAssociation[]>(
    []
  )

  useEffect(() => {
    if (isInitialized) {
      trackEvent({
        name: AmplitudeEvents.SEARCH_RESULTS_MOREFILTERS_VIEW
      })
    }
  }, [isInitialized])

  const close = () => {
    onClose()
  }

  const handleCounters = (name: string, value: number) => {
    setFilters((prev: IFilters) => ({ ...prev, [name]: value }))
  }

  const handleAmenities = (key: string) => {
    const isFiltered = filters?.amenities?.includes(key)
    if (isFiltered) {
      const amenitiesFiltered = filters?.amenities?.filter(
        (amenity: string) => amenity !== key
      )
      setFilters((prev: IFilters) => ({
        ...prev,
        amenities: amenitiesFiltered
      }))
    } else {
      setFilters((prev: IFilters) => ({
        ...prev,
        amenities: [...prev.amenities, key]
      }))
    }
  }

  const validateCharacteristics = () => {
    if (
      filters?.property_type?.length === 1 &&
      (filters?.property_type?.includes('TERRAIN') ||
        filters?.property_type?.includes('WAREHOUSE'))
    ) {
      return false
    }
    return true
  }

  const onInputNumberChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = target
    const formatValue = stringNumberFormatToSimpleNumber(value)
    setFilters((prev: IFilters) => ({
      ...prev,
      [name]: isNaN(formatValue) ? 0 : formatValue
    }))
  }

  const onChangeShowComission = (value: boolean) => {
    setFilters((prev: IFilters) => ({
      ...prev,
      showOnlyCommissionProperties: value
    }))
    if (isInitialized) {
      trackEvent({
        name: AmplitudeEvents.SEARCH_RESULTS_SHARECOMMISSION_ACTION,
        eventProperties: {
          kind: value
        }
      })
    }
  }
  const onCleanFilters = () => {
    resetMoreFilters()
    setRestartToggle(true)
    setTimeout(() => {
      setRestartToggle(false)
    }, 500)
  }
  useEffect(() => {
    if (associations?.length > 0) {
      if (hasMacrobolsaUser) {
        if (!isAdvance) {
          setAssociationsFilter(MACROBOLSA_ASSOCIATION_FILTER)
          return
        }
        const associationsArray = [
          ...associations,
          ...MACROBOLSA_ASSOCIATION_FILTER
        ]
        setAssociationsFilter([
          ...new Map(
            associationsArray.map((item) => [item.value, item])
          ).values()
        ])
      }
      setAssociationsFilter(associations)
      return
    }
    setAssociationsFilter([])
  }, [associations, hasMacrobolsaUser, isAdvance])

  const isLease = filters?.operation_type?.includes('LEASE')
  return (
    <Modal
      className="w-[525px] max-w-full max-h-[80%] pt-2"
      enableBgBlur
      onClose={close}
      isOpen={isOpen}
      isResponsive
      containerClassName="bg-black/50"
    >
      <Modal.Header title="Más filtros" />
      <Modal.Body className="flex items-center justify-between w-full pb-10 px-4 gap-2 h-auto flex-wrap overflow-y-auto max-h-fit">
        <div className="w-full flex flex-col">
          {associationsFilter?.length > 0 && (
            <>
              <SectionTitle title="Filtrar por asociación" />
              <Associations
                associations={associationsFilter}
                selectedAssociations={filters?.associations}
                onChangeAssociations={onChangeAssociations}
              />
            </>
          )}
        </div>
        {validateCharacteristics() && (
          <>
            <SectionTitle title="Características" />
            <DetailCounter
              name="Habitaciones"
              value={filters?.bedrooms}
              onChange={(value) => handleCounters('bedrooms', value)}
              minQuantity={0}
            />
            <DetailCounter
              name="Baños"
              value={filters?.bathrooms}
              onChange={(value) => handleCounters('bathrooms', value)}
              minQuantity={0}
            />
            <DetailCounter
              name="Estacionamientos"
              value={filters?.parkings}
              onChange={(value) => handleCounters('parkings', value)}
              minQuantity={0}
            />
            <Divider color="bg-surface-strokes" height="1px" />
          </>
        )}
        <SectionTitle title="Superficie" />
        <div className="w-full flex justify-between gap-2">
          <Input
            labelText="Mínima"
            classNameContainer="w-[48%]"
            name="surface_from"
            classNameLabel="text-secondary font-normal"
            value={filters?.surface_from?.toLocaleString()}
            onChange={onInputNumberChange}
            rightText="m²"
          />
          <Input
            labelText="Máxima"
            classNameContainer="w-[48%]"
            name="surface_to"
            classNameLabel="text-secondary font-normal"
            value={filters?.surface_to?.toLocaleString()}
            onChange={onInputNumberChange}
            rightText="m²"
          />
        </div>
        <Divider color="bg-surface-strokes" height="1px" />
        <SectionTitle title="Comisión" />
        <DetailCounter
          name={`${isLease ? 'Meses' : '%'} de comisión`}
          onChange={(value) => handleCounters('commission_rate_range', value)}
          value={filters?.commission_rate_range ?? 0}
          minQuantity={0}
        />
        {!hasMacrobolsaNotAdvancedUser && (
          <>
            <Divider color="bg-surface-strokes" height="1px" />
            <div className="flex flex-row justify-between items-center w-full">
              <SectionTitle title="Ver anuncios con comisión compartida" />
              {restartToggle ? (
                <Spinner color="terciary" className="h-[38px]" />
              ) : (
                <Switch
                  id="share-comsion-filter-switch"
                  onToggle={onChangeShowComission}
                  initialChecked={filters.showOnlyCommissionProperties}
                />
              )}
            </div>
          </>
        )}

        <Divider color="bg-surface-strokes" height="1px" />
        <SectionTitle title="Amenidades" />
        <div className="w-full flex flex-wrap gap-4">
          {AMENITIES_LIST?.map(({ title, icon, key }) => {
            const isChecked = filters?.amenities?.includes(key)
            return (
              <Checkbox
                key={key}
                id={key}
                type="checkbox"
                as="button"
                gap="gap-2"
                items="start"
                justify="between"
                containerClassName={composeClasses(
                  'flex-row-reverse w-[45%] sm:w-[48%] gap-2 bg-surface-white hover:bg-surface-gray-10 rounded-xl p-3.5 outline',
                  isChecked
                    ? 'outline-2 outline-primary'
                    : 'outline-1 outline-surface-strokes'
                )}
                isChecked={isChecked}
                inputClassName="h-[24px]"
                onChange={() => handleAmenities(key)}
                label={
                  <Flex dir="col" gap="gap-2">
                    <GoogleIcon
                      name={icon}
                      className="text-primary text-[24px] flex"
                    />
                    <p className="text-[15px] text-left text-primary font-normal leading-5">
                      {title}
                    </p>
                  </Flex>
                }
              />
            )
          })}
        </div>
      </Modal.Body>
      <Modal.Footer className="flex px-4 py-2 border-t border-surface-gray-10 w-full justify-between items-center gap-2">
        <OptionButton
          variant="gray"
          className="!pl-0 hover:bg-transparent"
          onClick={onCleanFilters}
        >
          <span className="underline decoration-solid">Limpiar selección</span>
        </OptionButton>
        <Button
          className="w-[200px]"
          variant="secondary"
          onClick={() => {
            applyMoreFilters()
            close()
            if (isInitialized) {
              trackEvent({
                name: AmplitudeEvents.SEARCH_RESULTS_MOREFILTERS_ACTION
              })
            }
          }}
        >
          Aceptar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
